import React from "react";
import { FlightOffer } from "../../types/flight";
import {
    MdLuggage,
    MdAirlineSeatReclineNormal,
    MdFlightTakeoff,
    MdWeekend,
    MdTrendingUp,
    MdRefresh,
    MdEditNote,
} from "react-icons/md";

// Type assertion to fix TypeScript issue with react-icons v5
const LuggageIcon = MdLuggage as any;
const SeatIcon = MdAirlineSeatReclineNormal as any;
const FlightIcon = MdFlightTakeoff as any;
const WeekendIcon = MdWeekend as any;
const TrendingIcon = MdTrendingUp as any;
const RefreshIcon = MdRefresh as any;
const EditNoteIcon = MdEditNote as any;

interface FareBrandDrawerProps {
    flight: FlightOffer;
    fareType: "economy" | "premium" | "business";
    onSelectBrand: (brandName: string, price: number) => void;
    onClose: () => void;
}

interface FareBrand {
    name: string;
    recommended?: boolean;
    seatsLeft?: number;
    features: {
        checkedBaggage: string;
        seatReservation: string;
        earnMiles: string;
        milesUpgrade?: string;
        loungeAccess?: string;
        flexibility: string[];
        note?: string;
    };
    price: number;
}

const getFareBrands = (
    fareType: "economy" | "premium" | "business",
    basePrice: number
): FareBrand[] => {
    if (fareType === "economy") {
        return [
            {
                name: "Economy Value",
                features: {
                    checkedBaggage: "20kg Included",
                    seatReservation: "Included*",
                    earnMiles: "75% (PR-operated flight)",
                    milesUpgrade: "Not Eligible",
                    flexibility: [
                        "Refund permitted with a fee",
                        "Change permitted with a fee",
                    ],
                },
                price: basePrice,
            },
            {
                name: "Economy Flex",
                recommended: true,
                features: {
                    checkedBaggage: "20kg Included",
                    seatReservation: "Included*",
                    earnMiles: "100% (PR-operated flight)",
                    milesUpgrade: "Eligible*",
                    flexibility: [
                        "Refund permitted - No fee",
                        "Change permitted with a fee",
                    ],
                },
                price: basePrice * 1.1,
            },
        ];
    } else if (fareType === "premium") {
        return [
            {
                name: "Comfort Class",
                seatsLeft: 3,
                features: {
                    checkedBaggage: "25kg Included",
                    seatReservation: "Included*",
                    earnMiles: "115% (PR-operated flight)",
                    loungeAccess: "Included*",
                    milesUpgrade: "Eligible*",
                    flexibility: [
                        "Refund permitted with a fee",
                        "Change permitted - No fee",
                    ],
                    note: "N9-tickets are not eligible for Miles Upgrade",
                },
                price: basePrice,
            },
        ];
    } else {
        return [
            {
                name: "Business Flex",
                recommended: true,
                features: {
                    checkedBaggage: "35kg Included",
                    seatReservation: "Included*",
                    earnMiles: "150% (PR-operated flight)",
                    loungeAccess: "Included*",
                    flexibility: [
                        "Refund permitted - No fee",
                        "Change permitted - No fee",
                    ],
                },
                price: basePrice,
            },
        ];
    }
};

const FareBrandDrawer: React.FC<FareBrandDrawerProps> = ({
    flight,
    fareType,
    onSelectBrand,
    onClose,
}) => {
    const currency = flight.price.currency;
    const basePrice = parseFloat(flight.price.total);

    // Get base price for the selected fare type
    const farePrices = {
        economy: basePrice,
        premium: basePrice,
        business: basePrice,
    };

    const brands = getFareBrands(fareType, farePrices[fareType]);

    const formatPrice = (amount: number): string => {
        return amount.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    };

    // Get background color based on fare type
    const getBackgroundColor = () => {
        switch (fareType) {
            case "economy":
                return "bg-[#e8f4ff]";
            case "premium":
                return "bg-[#f8e4e2]";
            case "business":
                return "bg-[#fff9e6]";
            default:
                return "bg-gray-50";
        }
    };

    const bgColor = getBackgroundColor();

    // For single fare brand (Premium Economy case), show simplified layout
    if (brands.length === 1) {
        const brand = brands[0];
        return (
            <div className={bgColor}>
                {/* Header */}
                <div className="px-6 py-4">
                    <div className="text-center">
                        <h2 className="text-lg font-bold text-[#0047ba] mb-1">
                            Select a fare
                        </h2>
                        <p className="text-sm text-[#0047ba] underline cursor-pointer">
                            What is included in each price? (*Applies to PR
                            operated flights)
                        </p>
                    </div>
                </div>

                {/* Single Fare Brand Card */}
                <div className={`px-2 pb-2 ${bgColor}`}>
                    <div className="max-w-xl mx-auto flex justify-center">
                        <div
                            className="bg-white rounded-lg shadow-md overflow-hidden relative mx-auto justify-center text-black"
                            style={{ width: "254px" }}
                        >
                            {/* Seats left badge */}
                            {/* {brand.seatsLeft && (
                                <div className="absolute top-0 right-0 bg-[#d4a574] text-white text-xs font-medium px-4 py-2 rounded-tl-lg">
                                    {brand.seatsLeft} Seats left
                                </div>
                            )} */}

                            <div className="p-3">
                                {/* Brand Name */}
                                <h3 className="text-lg font-bold text-[#0047ba] mb-6">
                                    {brand.name}
                                </h3>

                                {/* Features */}
                                <div className="space-y-4 mb-6">
                                    {/* Checked Baggage */}
                                    <div className="flex items-start gap-3">
                                        <LuggageIcon
                                            className="text-black"
                                            size={16}
                                        />
                                        <div>
                                            <div className="font-bold text-xs">
                                                Checked Baggage
                                            </div>
                                            <div className="text-xs pt-1">
                                                {brand.features.checkedBaggage}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Seat Reservation */}
                                    <div className="flex items-start gap-3">
                                        <SeatIcon
                                            className="text-black"
                                            size={16}
                                        />
                                        <div>
                                            <div className="font-bold text-xs">
                                                Seat Reservation
                                            </div>
                                            <div className="text-xs pt-1">
                                                {brand.features.seatReservation}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Earn Miles */}
                                    <div className="flex items-start gap-3">
                                        <FlightIcon
                                            className="text-black"
                                            size={16}
                                        />
                                        <div>
                                            <div className="font-bold text-xs">
                                                Earn Miles
                                            </div>
                                            <div className="text-xs pt-1">
                                                {brand.features.earnMiles}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Lounge Access (if applicable) */}
                                    {brand.features.loungeAccess && (
                                        <div className="flex items-start gap-3">
                                            <WeekendIcon
                                                className="text-black"
                                                size={16}
                                            />
                                            <div>
                                                <div className="font-bold text-xs">
                                                    Lounge Access
                                                </div>
                                                <div className="text-xs pt-1">
                                                    {
                                                        brand.features
                                                            .loungeAccess
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Miles Upgrade */}
                                    {brand.features.milesUpgrade && (
                                        <div className="flex items-start gap-3">
                                            <TrendingIcon
                                                className="text-black"
                                                size={16}
                                            />
                                            <div>
                                                <div className="font-bold text-xs">
                                                    Miles Upgrade
                                                </div>
                                                <div className="text-xs pt-1">
                                                    {
                                                        brand.features
                                                            .milesUpgrade
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Flexibility */}
                                    <div className="flex items-start gap-3">
                                        <RefreshIcon
                                            className="text-black"
                                            size={16}
                                        />
                                        <div>
                                            <div className="font-bold text-xs">
                                                Flexibility
                                            </div>
                                            {brand.features.flexibility.map(
                                                (item, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="text-xs pt-1"
                                                    >
                                                        {item}
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    {/* Note (if applicable) */}
                                    {brand.features.note && (
                                        <div className="flex items-start gap-3">
                                            <EditNoteIcon
                                                className="text-black"
                                                size={16}
                                            />
                                            <div>
                                                <div className="font-bold text-xs">
                                                    Note
                                                </div>
                                                <div className="text-xs pt-1">
                                                    {brand.features.note}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Price and Select Button */}
                                <div className="space-y-3">
                                    <div className="text-base font-medium text-black">
                                        {currency}{" "}
                                        <span className="font-bold text-xl">
                                            {formatPrice(brand.price)}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() =>
                                            onSelectBrand(
                                                brand.name,
                                                brand.price
                                            )
                                        }
                                        className="w-40 py-2 px-4 text-white text-base font-semibold rounded-lg bg-[#0047ba] hover:opacity-90 transition-opacity"
                                    >
                                        Select
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Close button */}
                <div className={`flex justify-center pb-4 ${bgColor}`}>
                    <button
                        onClick={onClose}
                        className="text-[#0047ba] hover:bg-white hover:bg-opacity-50 rounded-full p-2 transition-colors"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 15l7-7 7 7"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        );
    }

    // Multiple fare brands layout (Economy, Business)
    return (
        <div className={bgColor}>
            {/* Header */}
            <div className="px-6 py-4">
                <div className="text-center">
                    <h2 className="text-lg font-bold text-[#0047ba] mb-1">
                        Select a fare
                    </h2>
                    <p className="text-sm text-[#0047ba] underline cursor-pointer">
                        What is included in each price? (*Applies to PR operated
                        flights)
                    </p>
                </div>
            </div>

            {/* Fare Brand Cards */}
            <div className={`p-4 ${bgColor}`}>
                <div className="flex gap-4 justify-center">
                    {brands.map((brand, index) => (
                        <div
                            key={index}
                            className={`bg-white rounded-lg shadow-md overflow-hidden ${
                                brand.recommended ? "ring-2 ring-[#ff9800]" : ""
                            }`}
                            style={{
                                width: "254px",
                            }}
                        >
                            {/* Recommended Badge */}
                            {brand.recommended && (
                                <div className="bg-[#ff9800] text-white text-xs font-medium px-3 py-1 text-center">
                                    Recommended
                                </div>
                            )}

                            {/* Card Content */}
                            <div className="p-4">
                                {/* Brand Name */}
                                <h3 className="text-base font-bold text-[#0047ba] mb-3">
                                    {brand.name}
                                </h3>

                                {/* Features */}
                                <div className="space-y-2 mb-4 text-sm">
                                    {/* Checked Baggage */}
                                    <div className="flex items-start gap-2">
                                        <LuggageIcon
                                            className="text-black"
                                            size={16}
                                        />
                                        <div>
                                            <div className="font-semibold">
                                                Checked Baggage
                                            </div>
                                            <div className="text-gray-600">
                                                {brand.features.checkedBaggage}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Seat Reservation */}
                                    <div className="flex items-start gap-2">
                                        <SeatIcon
                                            className="text-black"
                                            size={16}
                                        />
                                        <div>
                                            <div className="font-semibold">
                                                Seat Reservation
                                            </div>
                                            <div className="text-gray-600">
                                                {brand.features.seatReservation}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Earn Miles */}
                                    <div className="flex items-start gap-2">
                                        <FlightIcon
                                            className="text-black"
                                            size={16}
                                        />
                                        <div>
                                            <div className="font-semibold">
                                                Earn Miles
                                            </div>
                                            <div className="text-gray-600">
                                                {brand.features.earnMiles}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Lounge Access (if applicable) */}
                                    {brand.features.loungeAccess && (
                                        <div className="flex items-start gap-2">
                                            <WeekendIcon
                                                className="text-black"
                                                size={16}
                                            />
                                            <div>
                                                <div className="font-semibold">
                                                    Lounge Access
                                                </div>
                                                <div className="text-gray-600">
                                                    {
                                                        brand.features
                                                            .loungeAccess
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Miles Upgrade */}
                                    {brand.features.milesUpgrade && (
                                        <div className="flex items-start gap-2">
                                            <TrendingIcon
                                                className="text-black"
                                                size={16}
                                            />
                                            <div>
                                                <div className="font-semibold">
                                                    Miles Upgrade
                                                </div>
                                                <div className="text-gray-600">
                                                    {
                                                        brand.features
                                                            .milesUpgrade
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Flexibility */}
                                    <div className="flex items-start gap-2">
                                        <RefreshIcon
                                            className="text-black"
                                            size={16}
                                        />
                                        <div>
                                            <div className="font-semibold">
                                                Flexibility
                                            </div>
                                            {brand.features.flexibility.map(
                                                (item, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="text-gray-600"
                                                    >
                                                        {item}
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    {/* Note (if applicable) */}
                                    {brand.features.note && (
                                        <div className="flex items-start gap-2">
                                            <EditNoteIcon
                                                className="text-black"
                                                size={16}
                                            />
                                            <div>
                                                <div className="font-semibold">
                                                    Note
                                                </div>
                                                <div className="text-gray-600">
                                                    {brand.features.note}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Price and Select Button */}
                                <div className="space-y-2">
                                    <div className="text-lg font-bold">
                                        {currency} {formatPrice(brand.price)}
                                    </div>
                                    <button
                                        onClick={() =>
                                            onSelectBrand(
                                                brand.name,
                                                brand.price
                                            )
                                        }
                                        className="w-full py-2 px-4 text-white font-semibold rounded-lg bg-[#0047ba] hover:opacity-90 transition-opacity"
                                    >
                                        Select
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Close button */}
            <div className={`flex justify-center pb-4 ${bgColor}`}>
                <button
                    onClick={onClose}
                    className="text-[#0047ba] hover:bg-white hover:bg-opacity-50 rounded-full p-2 transition-colors"
                >
                    <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default FareBrandDrawer;
